import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, LogOut } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

export function ChangePasswordForm({ onDone }) {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) { toast.error("The new passwords don't match"); return; }
    setBusy(true);
    try {
      await api.put("/me/password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password changed");
      setF({ current_password: "", new_password: "", confirm: "" });
      onDone?.();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
      <input type="password" required autoComplete="current-password" placeholder="Current password" value={f.current_password} onChange={set("current_password")} className={input} data-testid="current-password-input" />
      <input type="password" required autoComplete="new-password" placeholder="New password" value={f.new_password} onChange={set("new_password")} className={input} data-testid="new-password-input" />
      <input type="password" required autoComplete="new-password" placeholder="Type the new password again" value={f.confirm} onChange={set("confirm")} className={input} data-testid="confirm-password-input" />
      <p className="text-xs text-ink-muted">At least 10 characters, with capital and small letters and a number. Don't reuse a password from another website.</p>
      <button type="submit" disabled={busy} data-testid="change-password-submit" className="w-full rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-50">{busy ? "Saving…" : "Change password"}</button>
    </form>
  );
}

export function ChangePasswordCard() {
  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm" data-testid="change-password-card">
      <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><KeyRound size={18} className="text-brand" /> Change password</h2>
      <ChangePasswordForm />
    </div>
  );
}

export function ForcePasswordChange() {
  const { refreshUser, logout } = useAuth();
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="force-password-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-ink"><KeyRound size={20} className="text-brand" /> Please change your password</h2>
        <p className="mb-4 mt-1 text-sm text-ink-soft">To keep client and staff information safe, please choose a new, more secure password before you continue.</p>
        <ChangePasswordForm onDone={refreshUser} />
        <button onClick={logout} data-testid="force-password-logout" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-ink-muted hover:text-ink"><LogOut size={13} /> Sign out</button>
      </div>
    </div>
  );
}

export function PasswordRequestButton() {
  const send = async () => {
    if (!window.confirm("Send every befriender and office staff member an Inbox message asking them to change their password? They'll also be asked to change it as soon as they sign in.")) return;
    try { const r = await api.post("/staff/password-change-request"); toast.success(`Message sent to ${r.data.count} staff`); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <button onClick={send} data-testid="request-password-change-button" className="inline-flex items-center gap-2 rounded-lg border border-brand px-4 py-2.5 font-semibold text-brand hover:bg-brand-light">
      <KeyRound size={18} /> Ask all staff to change password
    </button>
  );
}
